// Iter 74 — Vendor Purchase Order detail page.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, FileText, Receipt, Plus, FileArrowUp, DownloadSimple, Trash,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    PO_STATUS_LABEL, PO_STATUS_BADGE, isPoLocked,
    TDS_LABEL, DOC_TYPE_LABEL, humanBytes,
    formatAmount, formatMarginPct,
} from "./vendor_constants";

export default function VendorPurchaseOrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [po, setPo] = useState(null);
    const [bills, setBills] = useState([]);
    const [allowedTransitions, setAllowedTransitions] = useState({});
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [billOpen, setBillOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get(`/vendor-purchase-orders/${id}`),
            api.get(`/vendor-bills`, { params: { vendor_purchase_order_id: id, page_size: 100 } }),
            api.get(`/vendor-purchase-orders/meta/enums`),
        ])
            .then(([pRes, bRes, mRes]) => {
                if (cancelled) return;
                setPo(pRes.data);
                setBills(bRes.data.items || []);
                setAllowedTransitions(mRes.data.allowed_transitions || {});
            })
            .catch((err) => {
                if (!cancelled) {
                    toast.error(formatApiError(err, "Failed to load PO"));
                    navigate("/vendor-purchase-orders");
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [id, navigate, reloadKey]);

    if (loading) return <div className="px-8 py-12 text-slate-500" data-testid="vpo-detail-loading">Loading…</div>;
    if (!po) return null;

    const badge = PO_STATUS_BADGE[po.status] || PO_STATUS_BADGE.draft;
    const locked = isPoLocked(po.status);
    const nextStates = allowedTransitions[po.status] || [];

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1300px]" data-testid="vpo-detail-page">
            <PageHeader
                eyebrow={`${po.code} · ${po.vendor_code} ${po.vendor_name}`}
                title={po.vendor_quotation_ref ? `PO for ${po.vendor_quotation_ref}` : `Purchase Order ${po.code}`}
                description={
                    <span className="inline-flex items-center gap-3 flex-wrap mt-1">
                        <Badge data-testid="vpo-status-badge" className={`${badge.className} hover:${badge.className}`}>
                            {badge.label}
                        </Badge>
                        <span className="text-xs text-slate-500">
                            {po.currency} · PO date {formatDate(po.po_date)}{po.expected_delivery_date ? ` · ETA ${formatDate(po.expected_delivery_date)}` : ""}
                        </span>
                        {locked && (
                            <Badge data-testid="vpo-locked-badge" variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                Financially locked
                            </Badge>
                        )}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button data-testid="vpo-back-btn" variant="outline" onClick={() => navigate(-1)} className="gap-1">
                            <ArrowLeft size={14} /> Back
                        </Button>
                        <Button data-testid="vpo-open-quote-btn" variant="outline"
                            onClick={() => navigate(`/vendor-quotations/${po.vendor_quotation_id}`)} className="gap-1">
                            <FileText size={14} /> Source Quote
                        </Button>
                        {can("vendor:write") && nextStates.length > 0 && nextStates.map((s) => (
                            <Button key={s} data-testid={`vpo-status-to-${s}`} size="sm"
                                variant={s === "issued" ? "default" : "outline"}
                                onClick={() => { setPendingStatus(s); setStatusOpen(true); }}>
                                Mark {PO_STATUS_LABEL[s]}
                            </Button>
                        ))}
                        {can("vendor:write") && po.status !== "draft" && po.status !== "cancelled" && (
                            <Button data-testid="vpo-generate-bill-btn" size="sm"
                                onClick={() => setBillOpen(true)} className="gap-1">
                                <Receipt size={14} /> Generate Bill
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-5">
                    {/* Billing progress */}
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vpo-progress-panel">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-slate-900">Billing Progress</h3>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">
                                {po.bill_count || 0} bill(s) recorded
                            </Badge>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <Stat label="PO Grand Total" value={formatAmount(po.grand_total, po.currency)} />
                            <Stat label="Billed" value={formatAmount(po.billed_amount, po.currency)} tone="positive" />
                            <Stat label="Remaining" value={formatAmount(Math.max(0, (po.grand_total || 0) - (po.billed_amount || 0)), po.currency)} />
                            <Stat label="Progress" value={`${Number(po.billing_progress_pct || 0).toFixed(2)}%`} testid="vpo-progress-pct" />
                        </div>
                        <div className="mt-3 w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, po.billing_progress_pct || 0)}%` }} />
                        </div>
                    </div>

                    {/* Bills against this PO */}
                    <Section title="Bills Against This PO">
                        {bills.length === 0 ? (
                            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-md" data-testid="vpo-bills-empty">
                                No bills recorded yet
                            </div>
                        ) : (
                            <div className="overflow-x-auto" data-testid="vpo-bills-table">
                                <table className="w-full text-sm">
                                    <thead className="text-slate-600 text-xs">
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left px-3 py-2 font-medium">Code</th>
                                            <th className="text-left px-3 py-2 font-medium">Bill #</th>
                                            <th className="text-left px-3 py-2 font-medium">Bill Date</th>
                                            <th className="text-right px-3 py-2 font-medium">Gross</th>
                                            <th className="text-right px-3 py-2 font-medium">Net Payable</th>
                                            <th className="text-right px-3 py-2 font-medium">Paid</th>
                                            <th className="text-left px-3 py-2 font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bills.map((b) => (
                                            <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                                                onClick={() => navigate(`/vendor-bills/${b.id}`)} data-testid={`vpo-bill-${b.code}`}>
                                                <td className="px-3 py-2 font-mono text-xs">{b.code}</td>
                                                <td className="px-3 py-2 text-xs text-slate-700">{b.bill_number}</td>
                                                <td className="px-3 py-2 text-xs text-slate-700">{formatDate(b.bill_date)}</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{formatAmount(b.gross_amount, b.currency)}</td>
                                                <td className="px-3 py-2 text-right font-mono">{formatAmount(b.net_payable, b.currency)}</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">{formatAmount(b.amount_paid, b.currency)}</td>
                                                <td className="px-3 py-2 text-xs capitalize">{(b.status || "").replace(/_/g, " ")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Section>

                    {/* PO lines */}
                    <Section title="Line Items">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-slate-600 text-xs">
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left px-3 py-2 font-medium">Service</th>
                                        <th className="text-right px-3 py-2 font-medium">Qty</th>
                                        <th className="text-right px-3 py-2 font-medium">Unit Price</th>
                                        <th className="text-right px-3 py-2 font-medium">GST %</th>
                                        <th className="text-right px-3 py-2 font-medium">Line Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(po.lines || []).map((ln) => (
                                        <tr key={ln.id} className="border-b border-slate-100">
                                            <td className="px-3 py-2">
                                                <div className="font-medium text-slate-900 text-xs">{ln.vendor_service_code}</div>
                                                <div className="text-xs text-slate-600 mt-0.5">{ln.vendor_service_name}</div>
                                                {ln.description && <div className="text-[11px] text-slate-500 mt-0.5">{ln.description}</div>}
                                            </td>
                                            <td className="px-3 py-2 text-right font-mono text-xs">{ln.quantity}</td>
                                            <td className="px-3 py-2 text-right font-mono text-xs">{formatAmount(ln.unit_price, po.currency)}</td>
                                            <td className="px-3 py-2 text-right font-mono text-xs">{Number(ln.gst_rate || 0).toFixed(2)}%</td>
                                            <td className="px-3 py-2 text-right font-mono text-sm">{formatAmount(ln.line_total, po.currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="text-xs text-slate-700">
                                    <tr className="border-t border-slate-200">
                                        <td colSpan={4} className="text-right px-3 py-2 text-slate-500">Subtotal</td>
                                        <td className="text-right px-3 py-2 font-mono">{formatAmount(po.subtotal, po.currency)}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={4} className="text-right px-3 py-2 text-slate-500">Per-line GST</td>
                                        <td className="text-right px-3 py-2 font-mono">{formatAmount(po.line_gst_total, po.currency)}</td>
                                    </tr>
                                    {Number(po.discount_amount) > 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-right px-3 py-2 text-rose-600">Less: Discount</td>
                                            <td className="text-right px-3 py-2 font-mono text-rose-600">-{formatAmount(po.discount_amount, po.currency)}</td>
                                        </tr>
                                    )}
                                    <tr className="border-t border-slate-200">
                                        <td colSpan={4} className="text-right px-3 py-3 font-semibold">Grand Total</td>
                                        <td className="text-right px-3 py-3 font-mono font-semibold" data-testid="vpo-grand-total">{formatAmount(po.grand_total, po.currency)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </Section>
                </div>

                <aside className="lg:col-span-1 space-y-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vpo-meta-card">
                        <h3 className="font-medium text-slate-900 mb-3">Record metadata</h3>
                        <div className="space-y-2.5 text-xs">
                            <MetaRow label="Internal ID" value={po.id} mono />
                            <MetaRow label="Display code" value={po.code} mono />
                            <MetaRow label="From Quote" value={`${po.vendor_quotation_code}`} mono />
                            <MetaRow label="Currency" value={po.currency} />
                            <MetaRow label="Created" value={formatDate(po.created_at)} />
                            <MetaRow label="Created by" value={po.created_by_email} />
                            {po.issued_at && <MetaRow label="Issued" value={formatDate(po.issued_at)} />}
                            {po.cancelled_at && <MetaRow label="Cancelled" value={formatDate(po.cancelled_at)} />}
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vpo-tds-card">
                        <h3 className="font-medium text-slate-900 mb-3">TDS Snapshot</h3>
                        <div className="space-y-2.5 text-xs">
                            <MetaRow label="Applicable" value={po.tds_applicable ? "Yes" : "No"} />
                            <MetaRow label="Section" value={TDS_LABEL[po.tds_section] || po.tds_section || "—"} />
                            <MetaRow label="Rate" value={`${Number(po.tds_rate || 0).toFixed(2)}%`} />
                        </div>
                    </div>

                    {po.mapped_line_count > 0 && (
                        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="vpo-margin-card">
                            <h3 className="font-medium text-slate-900 mb-3">Margin (from quote)</h3>
                            <div className="space-y-2.5 text-xs">
                                <MetaRow label="Catalogue Value" value={formatAmount(po.total_catalogue_value, po.currency)} />
                                <MetaRow label="Mapped Cost" value={formatAmount(po.mapped_cost_total, po.currency)} />
                                <MetaRow label="Margin" value={formatAmount(po.margin_amount, po.currency)} />
                                <MetaRow label="Margin %" value={formatMarginPct(po.margin_pct)} />
                            </div>
                        </div>
                    )}

                    {po.cancellation_reason && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-5">
                            <h3 className="font-medium text-rose-900 mb-2 text-sm">Cancellation Reason</h3>
                            <p className="text-xs text-rose-800">{po.cancellation_reason}</p>
                        </div>
                    )}
                </aside>
            </div>

            <GenerateBillDialog
                key={billOpen ? "open" : "closed"}
                open={billOpen}
                onClose={() => setBillOpen(false)}
                onSaved={(billId) => { reload(); navigate(`/vendor-bills/${billId}`); }}
                po={po}
            />
            <StatusDialog
                open={statusOpen}
                onClose={() => setStatusOpen(false)}
                onSaved={reload}
                po={po}
                nextStatus={pendingStatus}
            />
        </div>
    );
}

function Stat({ label, value, tone, testid }) {
    const toneClass = tone === "positive" ? "text-emerald-700" : "text-slate-900";
    return (
        <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
            <div data-testid={testid} className={`mt-1 font-semibold font-mono ${toneClass}`}>{value}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-medium text-slate-900 mb-4">{title}</h3>
            {children}
        </div>
    );
}

function MetaRow({ label, value, mono }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="text-slate-500 shrink-0">{label}</div>
            <div className={`text-slate-900 text-right break-all ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
        </div>
    );
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };

function GenerateBillDialog({ open, onClose, onSaved, po }) {
    const remainingSub = Math.max(0, (po.subtotal || 0) - 0); // operator can over/underbill
    const remainingGst = Math.max(0, (po.line_gst_total || 0) - 0);
    const [form, setForm] = useState(() => ({
        bill_number: "",
        bill_date: todayISO(),
        due_date: addDaysISO(30),
        description: "",
        subtotal: remainingSub,
        gst_amount: remainingGst,
        tds_amount: "",
        notes: "",
    }));
    const [busy, setBusy] = useState(false);

    const setField = (k) => (e) => {
        const v = e?.target?.type === "number" ? e.target.value : e?.target?.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const computed = {
        sub: Number(form.subtotal || 0),
        gst: Number(form.gst_amount || 0),
        gross: Number(form.subtotal || 0) + Number(form.gst_amount || 0),
    };
    const tdsAuto = Math.round((computed.sub * Number(po.tds_rate || 0) / 100) * 100) / 100;
    const tdsActual = form.tds_amount === "" ? tdsAuto : Number(form.tds_amount || 0);
    const netPayable = Math.round((computed.gross - tdsActual) * 100) / 100;

    const submit = async () => {
        if (!form.bill_number.trim()) { toast.error("Bill number is required"); return; }
        if (!form.bill_date || !form.due_date) { toast.error("Bill date and due date are required"); return; }
        setBusy(true);
        try {
            const payload = {
                bill_number: form.bill_number,
                bill_date: form.bill_date,
                due_date: form.due_date,
                description: form.description,
                subtotal: Number(form.subtotal || 0),
                gst_amount: Number(form.gst_amount || 0),
                tds_amount: form.tds_amount === "" ? null : Number(form.tds_amount),
                notes: form.notes,
            };
            const r = await api.post(`/vendor-purchase-orders/${po.id}/bills`, payload);
            toast.success(`Bill ${r.data.code} created`);
            onSaved(r.data.id);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err, "Bill creation failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="vpo-bill-dialog">
                <DialogHeader>
                    <DialogTitle>Generate Bill from PO</DialogTitle>
                    <DialogDescription>
                        Auto-populated from {po.code}. TDS ({po.tds_section} @ {Number(po.tds_rate || 0).toFixed(2)}%) is pre-computed; override below if needed.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
                    <Field label="Bill Number *" colSpan={2}>
                        <Input data-testid="bill-number" value={form.bill_number} onChange={setField("bill_number")} placeholder="Vendor's invoice ref" />
                    </Field>
                    <Field label="Bill Date *">
                        <Input data-testid="bill-date" type="date" value={form.bill_date} onChange={setField("bill_date")} />
                    </Field>
                    <Field label="Due Date *">
                        <Input data-testid="bill-due-date" type="date" value={form.due_date} onChange={setField("due_date")} />
                    </Field>
                    <Field label="Subtotal *">
                        <Input data-testid="bill-subtotal" type="number" min={0} step="0.01" value={form.subtotal} onChange={setField("subtotal")} />
                    </Field>
                    <Field label="GST Amount">
                        <Input data-testid="bill-gst" type="number" min={0} step="0.01" value={form.gst_amount} onChange={setField("gst_amount")} />
                    </Field>
                    <Field label={`TDS Amount (auto: ${formatAmount(tdsAuto, po.currency)})`} hint="Leave blank to auto-compute">
                        <Input data-testid="bill-tds-override" type="number" min={0} step="0.01" value={form.tds_amount} onChange={setField("tds_amount")} placeholder={`${tdsAuto}`} />
                    </Field>
                    <Field label="Description" colSpan={2}>
                        <Input data-testid="bill-desc" value={form.description} onChange={setField("description")} />
                    </Field>
                    <Field label="Notes" colSpan={2}>
                        <Textarea data-testid="bill-notes" rows={2} value={form.notes} onChange={setField("notes")} />
                    </Field>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 mt-2" data-testid="bill-totals-preview">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        <Stat label="Gross" value={formatAmount(computed.gross, po.currency)} />
                        <Stat label="TDS" value={formatAmount(tdsActual, po.currency)} />
                        <Stat label="Net Payable" value={formatAmount(netPayable, po.currency)} tone="positive" testid="bill-preview-net" />
                        <Stat label="Currency" value={po.currency} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="bill-save-btn" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Create Bill"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, hint, children, colSpan }) {
    return (
        <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}

function StatusDialog({ open, onClose, onSaved, po, nextStatus }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    if (!po || !nextStatus) return null;
    const needsReason = nextStatus === "cancelled";
    const submit = async () => {
        if (needsReason && !reason.trim()) { toast.error("Reason required when cancelling"); return; }
        setBusy(true);
        try {
            await api.patch(`/vendor-purchase-orders/${po.id}/status`, { status: nextStatus, reason });
            toast.success(`Status changed to ${PO_STATUS_LABEL[nextStatus]}`);
            onSaved();
            onClose();
            setReason("");
        } catch (err) {
            toast.error(formatApiError(err, "Status change failed"));
        } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="vpo-status-dialog">
                <DialogHeader>
                    <DialogTitle>Change PO Status</DialogTitle>
                    <DialogDescription>
                        {po.code} · {PO_STATUS_LABEL[po.status]} → <strong>{PO_STATUS_LABEL[nextStatus]}</strong>
                        {nextStatus === "issued" && " — financials will be locked."}
                    </DialogDescription>
                </DialogHeader>
                {needsReason && (
                    <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Cancellation reason *</Label>
                        <Textarea data-testid="vpo-status-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button data-testid="vpo-status-confirm" onClick={submit} disabled={busy}>{busy ? "Saving…" : `Mark ${PO_STATUS_LABEL[nextStatus]}`}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
